import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import debounce from 'lodash.debounce';
import { Button, DatePicker, Form, Input, message, Modal, Spin, Tag } from 'antd';
import ReactQuill from 'react-quill';
import taskerTemplateApi from '@/api/taskerTemplate';
import { disabledDate } from '@/utils/publicMethod';
import { calendarIcon, searchIcon } from '@/constants/static';
import 'react-quill/dist/quill.snow.css';
import './TaskerTemplate.scss';

const INITIAL_VISIBLE_COUNT = 6;
const CONTENT_PREVIEW_MIN_CHARS = 120;

const stripHtmlToText = (html: string) =>
    html
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const isLongTemplateContent = (html: string) => stripHtmlToText(html).length > CONTENT_PREVIEW_MIN_CHARS;

type TaskerTemplateRow = {
    taskerTemplateId: string;
    title: string;
    content: string;
    isDefault: boolean;
    createTime: string;
    updateTime: string;
};

type TemplateFormValues = {
    title: string;
    content: string;
};

const supportedVariables = [
    '{{TaskerName}}',
    '{{TaskerFirstName}}',
    '{{TaskerLastName}}',
    '{{TaskerEmail}}',
    '{{TaskerMobile}}',
    '{{TaskerId}}',
    '{{AssemblyDate}}',
];

const quillModules = {
    toolbar: [
        ['bold', 'italic', 'underline'],
        [{ size: [] }, { font: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['link', 'image'],
        ['clean'],
    ],
};

const quillFormats = ['bold', 'italic', 'underline', 'size', 'font', 'list', 'bullet', 'align', 'link', 'image'];

type TaskerTemplateGridCardProps = {
    row: TaskerTemplateRow;
    onEdit: () => void;
    onDelete: () => void;
};

const TaskerTemplateGridCard = ({ row, onEdit, onDelete }: TaskerTemplateGridCardProps) => {
    const [contentExpanded, setContentExpanded] = useState(false);
    const showContentToggle = isLongTemplateContent(row.content || '');
    const previewClass = contentExpanded ? 'card-preview is-expanded' : 'card-preview is-collapsed';

    const handleToggleContent = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setContentExpanded(prev => !prev);
    };

    return (
        <div className="tasker-template-card">
            <div className="card-header">
                <div className="card-title">
                    {row.title}
                    {row.isDefault ? (
                        <Tag color="blue" style={{ marginLeft: 8 }}>
                            Default
                        </Tag>
                    ) : null}
                </div>
                <div className="card-actions">
                    <img src={require('@/assets/images/edit-icon.png')} alt="edit" onClick={onEdit} />
                    <img src={require('@/assets/images/delete-icon.png')} alt="delete" onClick={onDelete} />
                </div>
            </div>
            <div className={previewClass} dangerouslySetInnerHTML={{ __html: row.content || '' }} />
            <div className="card-footer">
                <div className="card-meta">
                    Created: {row.createTime ? dayjs(row.createTime).format('YYYY-MM-DD HH:mm') : '-'}
                </div>
                {showContentToggle ? (
                    <Button
                        type="default"
                        htmlType="button"
                        className="card-content-toggle"
                        onClick={handleToggleContent}
                    >
                        {contentExpanded ? 'Show hide' : 'Show more'}
                    </Button>
                ) : null}
            </div>
        </div>
    );
};

const TaskerTemplate = () => {
    const [form] = Form.useForm<TemplateFormValues>();
    const quillRef = useRef<ReactQuill | null>(null);
    const [templateId, setTemplateId] = useState('');
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingRow, setEditingRow] = useState<TaskerTemplateRow | null>(null);
    const [saving, setSaving] = useState(false);
    const { RangePicker } = DatePicker;
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<TaskerTemplateRow[]>([]);
    const [listExpanded, setListExpanded] = useState(false);
    const [listParams, setListParams] = useState({
        startTime: '',
        endTime: '',
        keyword: '',
    });

    const debouncedKeyword = useMemo(
        () =>
            debounce((keyword: string) => {
                setListParams(prev => ({ ...prev, keyword }));
            }, 1500),
        []
    );
    const inputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        debouncedKeyword(e.target.value);
    };

    const getList = useCallback(async () => {
        setLoading(true);
        try {
            const res = await taskerTemplateApi.getList(listParams);
            setRows(res.data || []);
        } catch (error: unknown) {
            message.error(error instanceof Error ? error.message : String(error));
        } finally {
            setLoading(false);
        }
    }, [listParams]);

    useEffect(() => {
        void getList();
    }, [getList]);

    useEffect(() => {
        setListExpanded(false);
    }, [listParams]);

    const visibleRows = useMemo(
        () => (listExpanded ? rows : rows.slice(0, INITIAL_VISIBLE_COUNT)),
        [listExpanded, rows]
    );
    const hiddenCount = Math.max(0, rows.length - INITIAL_VISIBLE_COUNT);
    const showListToggle = rows.length > INITIAL_VISIBLE_COUNT;

    const dateOnChange = (dates: null | [Dayjs | null, Dayjs | null]) => {
        const start = dates?.[0];
        const end = dates?.[1];
        if (!start || !end) {
            setListParams(prev => ({ ...prev, startTime: '', endTime: '' }));
            return;
        }
        setListParams(prev => ({
            ...prev,
            startTime: dayjs(`${start.format('YYYY-MM-DD')} 00:00:00`).toISOString(),
            endTime: dayjs(`${end.format('YYYY-MM-DD')} 23:59:59`).toISOString(),
        }));
    };

    const closeDeleteModal = () => setOpenDeleteModal(false);

    const openAddModal = () => {
        form.resetFields();
        setEditingRow(null);
        form.setFieldsValue({ title: '', content: '' });
        setShowAddModal(true);
    };

    const openEditModal = (row: TaskerTemplateRow) => {
        form.resetFields();
        setEditingRow(row);
        form.setFieldsValue({
            title: row.title,
            content: row.content,
        });
        setShowAddModal(true);
    };

    const closeAddModal = () => {
        setShowAddModal(false);
        setEditingRow(null);
        form.resetFields();
    };

    const saveTemplate = async (values: TemplateFormValues) => {
        setSaving(true);
        try {
            const params = {
                title: values.title.trim(),
                content: values.content.trim(),
            };
            if (editingRow) {
                await taskerTemplateApi.update(editingRow.taskerTemplateId, params);
                message.success('Template updated');
            } else {
                await taskerTemplateApi.create(params);
                message.success('Template created');
            }
            closeAddModal();
            getList();
        } catch (error: unknown) {
            message.error(error instanceof Error ? error.message : String(error));
        } finally {
            setSaving(false);
        }
    };

    const deleteRow = async () => {
        try {
            await taskerTemplateApi.delete(templateId);
            getList();
            setTemplateId('');
            setOpenDeleteModal(false);
            message.success('Template deleted');
        } catch (error: unknown) {
            message.error(error instanceof Error ? error.message : String(error));
        }
    };

    const insertVariable = (variable: string) => {
        const editor = quillRef.current?.getEditor();
        if (!editor) return;
        editor.focus();
        const selection = editor.getSelection(true);
        const index = selection?.index ?? editor.getLength();
        editor.insertText(index, variable);
        editor.setSelection(index + variable.length, 0);
    };

    return (
        <div className="page-main Categories tasker-template-page">
            <div className="page-title flex-between">
                B2B Message templates
                <Button
                    className="flex-center"
                    style={{ height: 46, minWidth: 123, padding: '0 16px', borderRadius: 8 }}
                    type="primary"
                    onClick={openAddModal}
                >
                    <img src={require('@/assets/images/add-icon.png')} alt="" /> Add new
                </Button>
            </div>

            <div className="select-bar" style={{ marginBottom: 16 }}>
                <RangePicker
                    className="search-input"
                    placeholder={['dd/mm/yyyy', 'dd/mm/yyyy']}
                    suffixIcon={calendarIcon}
                    separator={'-'}
                    format={'DD/MM/YYYY'}
                    disabledDate={disabledDate}
                    onChange={dateOnChange}
                />
                <Input
                    className="search-input"
                    placeholder="Search by title"
                    onChange={inputChange}
                    prefix={searchIcon}
                />
            </div>

            {loading ? (
                <div className="data_loading flex-center">
                    <Spin />
                </div>
            ) : rows.length ? (
                <>
                    <div className="tasker-template-grid">
                        {visibleRows.map(row => (
                            <TaskerTemplateGridCard
                                key={String(row.taskerTemplateId)}
                                row={row}
                                onEdit={() => openEditModal(row)}
                                onDelete={() => {
                                    setOpenDeleteModal(true);
                                    setTemplateId(row.taskerTemplateId);
                                }}
                            />
                        ))}
                    </div>
                    {showListToggle ? (
                        <div className="tasker-template-grid-toggle">
                            <Button type="default" onClick={() => setListExpanded(prev => !prev)}>
                                {listExpanded ? 'Show hide' : `Show more (${hiddenCount})`}
                            </Button>
                        </div>
                    ) : null}
                </>
            ) : (
                <div className="no-data flex-center">No data</div>
            )}

            <Modal
                width={720}
                className="tasker-category-modal"
                maskClosable={false}
                centered={true}
                closable={false}
                open={showAddModal}
                onCancel={closeAddModal}
                footer={null}
            >
                <div className="modal-title">
                    {editingRow ? 'Edit B2B message template' : 'Add B2B message template'}
                </div>
                <Form form={form} layout="vertical" onFinish={saveTemplate} initialValues={{ title: '', content: '' }}>
                    <Form.Item label="Available variables" style={{ marginBottom: 8 }}>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {supportedVariables.map(variable => (
                                <Tag
                                    key={variable}
                                    style={{ marginInlineEnd: 0, cursor: 'pointer' }}
                                    onClick={() => insertVariable(variable)}
                                >
                                    {variable}
                                </Tag>
                            ))}
                        </div>
                    </Form.Item>
                    <Form.Item label="Title" name="title" rules={[{ required: true, message: 'Please enter title' }]}>
                        <Input placeholder="Template title" maxLength={256} />
                    </Form.Item>
                    <Form.Item
                        label="Message"
                        name="content"
                        rules={[{ required: true, message: 'Please enter message content' }]}
                    >
                        <ReactQuill
                            ref={quillRef}
                            theme="snow"
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Template message content"
                            style={{ height: 300, marginBottom: 42 }}
                        />
                    </Form.Item>
                    <div className="flex-center" style={{ marginTop: 4, columnGap: 16 }}>
                        <Button style={{ width: 132, height: 44 }} onClick={closeAddModal} className="cancel-btn">
                            Cancel
                        </Button>
                        <Button style={{ width: 132, height: 44 }} type="primary" htmlType="submit" loading={saving}>
                            Save
                        </Button>
                    </div>
                </Form>
            </Modal>

            <Modal
                width={408}
                className="tasker-category-modal tasker-category-delete-modal"
                maskClosable={false}
                centered={true}
                closable={false}
                open={openDeleteModal}
                onCancel={closeDeleteModal}
                footer={null}
            >
                <div className="modal-title">Delete B2B message template</div>
                <div className="modal-tip">Are you sure you want to delete this?</div>
                <div className="flex-center" style={{ marginTop: 24, columnGap: 16 }}>
                    <Button style={{ width: 132, height: 44 }} onClick={closeDeleteModal} className="cancel-btn">
                        Cancel
                    </Button>
                    <Button style={{ width: 132, height: 44 }} danger type="primary" onClick={deleteRow}>
                        Yes, delete
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

export default TaskerTemplate;
